import React, { useEffect, useState } from "react";

const pad = (n) => String(n).padStart(2, "0");

const toDateInputValue = (date = new Date()) => {
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  return `${y}-${m}-${d}`; // YYYY-MM-DD
};

/**
 * Datepicket
 * Props:
 * - value: string in YYYY-MM-DD (controlled). If omitted component is uncontrolled.
 * - onChange: (newDateString) => void
 * - minDate: string in YYYY-MM-DD to override default min (defaults to today)
 * - required: boolean
 * - label: optional label text
 * - className: optional wrapper classes
 */
const Datepicker = ({
  value,
  onChange,
  minDate,
  required = false,
  label,
  className = "",
  id,
}) => {
  const todayStr = toDateInputValue();
  const resolvedMin = minDate;
  //const resolvedMin = minDate || todayStr;

  const [internalValue, setInternalValue] = useState(value ?? "");
  const [error, setError] = useState("");

  // keep internal in sync when controlled
  useEffect(() => {
    if (value !== undefined) setInternalValue(value);
  }, [value]);

  const validate = (dateStr) => {
    if (!dateStr) {
      if (required) return "Date is required.";
      return "";
    }
    // comparing YYYY-MM-DD strings works lexicographically
    if (resolvedMin && dateStr < resolvedMin) {
      return `Date must be ${resolvedMin} or later.`;
    }
    return "";
  };

  const handleChange = (e) => {
    const next = e.target.value;
    // update local state always (controlled behaviour if parent provides value should override)
    setInternalValue(next);
    const validationError = validate(next);
    setError(validationError);
    if (!validationError && typeof onChange === "function") {
      onChange(next);
    } else if (typeof onChange === "function") {
      // still notify parent so it can react to invalid/empty if desired
      onChange(next);
    }
  };

  const handleBlur = () => {
    const validationError = validate(internalValue);
    setError(validationError);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <input
        id={id}
        type="date"
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        min={resolvedMin}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`block w-full rounded-md border px-3 py-2 bg-white text-sm
          focus:outline-none focus:ring-2 focus:ring-primary/60
          ${error ? "border-red-500" : "border-gray-300"}
          dark:bg-gray-800 dark:text-gray-100`}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-2 text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default Datepicker;